import type { GetTipiDomandaParams } from '@framework/api-client';
import { omit } from 'lodash';
import { useMemo } from 'react';
import { useLockedRecipientTypeFilter } from './useLockedRecipientTypeFilter';

export interface UseRecipientLockedQueryResult {
  /** Id del tipo utente destinatario forzato, o undefined se nessun blocco. */
  lockedRecipientType: string[] | undefined;
  /** true quando il filtro Destinatario è forzato dalla sessione. */
  isRecipientTypeLocked: boolean;
  /**
   * Query controllata dall'utente, SENZA il lock forzato. È il percorso di
   * scrittura: la usiamo per persistere lo stato in URL (`setQuery`) e per
   * rilevare i filtri attivi (`isFiltered`), così il lock derivato dalla
   * sessione non finisce in URL né viene conteggiato come filtro utente.
   */
  baseQuery: GetTipiDomandaParams;
  /**
   * `baseQuery` + il lock forzato. È il percorso di lettura: la usiamo per la
   * chiamata al backend e come `initialValues` del form, così il filtro
   * Destinatario risulta applicato anche se non è persistito in URL.
   */
  effectiveQuery: GetTipiDomandaParams;
}

/**
 * Deriva dalla query corrente le due viste necessarie quando il filtro
 * Destinatario è bloccato dalla sessione "per conto di" (cfr.
 * {@link useLockedRecipientTypeFilter}): `baseQuery` (scrittura/URL) ed
 * `effectiveQuery` (lettura/backend). In assenza di lock le due coincidono.
 */
export function useRecipientLockedQuery(
  query: GetTipiDomandaParams,
): UseRecipientLockedQueryResult {
  const { lockedRecipientType } = useLockedRecipientTypeFilter();
  const isRecipientTypeLocked = Boolean(lockedRecipientType);

  const baseQuery = useMemo(
    () =>
      isRecipientTypeLocked ? omit(query, 'tipoUtenteDestinatario') : query,
    [isRecipientTypeLocked, query],
  );

  const effectiveQuery = useMemo(
    () =>
      lockedRecipientType
        ? { ...baseQuery, tipoUtenteDestinatario: lockedRecipientType }
        : baseQuery,
    [lockedRecipientType, baseQuery],
  );

  return {
    lockedRecipientType,
    isRecipientTypeLocked,
    baseQuery,
    effectiveQuery,
  };
}
